import React, { useEffect, useState, useCallback } from "react";
import API_BASE_URL from "../../../../Constants";
import SearchIcon from "@mui/icons-material/Search";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";

import { proposalListFields } from "./config";
import ViewEditProposalsModal from "./ViewEditModal";

// Import Chart.js components
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const DashboardSidePanel = ({ proposals }) => {
  const pendingCount = proposals.filter((p) => p.proposalStatus  === "New").length;
  const approvedCount = proposals.filter((p) => p.proposalStatus  === "Accepted").length;

  const classStatusCounts = {};
  proposals.forEach((proposal) => {
    const cls = proposal.proposalSubClass?.proposalClass?.className || "Other";
    const status = proposal.proposalStatus || "unknown";
    if (!classStatusCounts[cls]) {
      classStatusCounts[cls] = {};
    }
    classStatusCounts[cls][status] = (classStatusCounts[cls][status] || 0) + 1;
  });

  const classes = Object.keys(classStatusCounts);
  const allStatusesSet = new Set();
  classes.forEach((cls) => {
    Object.keys(classStatusCounts[cls]).forEach((status) => allStatusesSet.add(status));
  });
  const allStatuses = Array.from(allStatusesSet);

  // Predefined colors for statuses (cycles if more statuses than colors)
  const statusColors = [
    "#FF6384",
    "#36A2EB",
    "#FFCE56",
    "#4CAF50",
    "#9C27B0",
    "#F44336",
    "#42A5F5",
    "#66BB6A",
    "#FFA726",
  ];

  const groupedBarData = {
    labels: classes,
    datasets: allStatuses.map((status, i) => ({
      label: status,
      data: classes.map((cls) => classStatusCounts[cls][status] || 0),
      backgroundColor: statusColors[i % statusColors.length],
    })),
  };

  // Compute overall status counts for a Pie Chart (optional)
  const statusCounts = proposals.reduce((acc, proposal) => {
    const status = proposal.status || "unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const inprogress = 0;
  const expired = 0;

  const [selectedClass, setSelectedClass] = useState(null);

  const handleBarClick = (event, elements) => {
    if (elements.length > 0) {
      const clickedClass = groupedBarData.labels[elements[0].index];
      setSelectedClass(clickedClass);
    }
  };

  const subclassData = selectedClass
    ? proposals.filter(p => p.proposalSubClass?.proposalClass?.className === selectedClass)
        .reduce((acc, proposal) => {
          const subclass = proposal.proposalSubClass?.subClassName || "Other";
          acc[subclass] = (acc[subclass] || 0) + 1;
          return acc;
        }, {})
    : null;

  const subclassChartData = subclassData
    ? {
        labels: Object.keys(subclassData),
        datasets: [
          {
            label: `Subclasses of ${selectedClass}`,
            data: Object.values(subclassData),
            backgroundColor: statusColors,
          },
        ],
      }
    : null;

  return (
    <div className="bg-white p-4 rounded-lg shadow-lg mb-4">
      <h3 className="text-2xl font-bold mb-4 text-center">Dashboard Summary</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-100 p-4 rounded-lg">
          <p className="font-semibold text-lg">Pending</p>
          <p className="text-2xl">{pendingCount}</p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <p className="font-semibold text-lg">Approved</p>
          <p className="text-2xl">{approvedCount}</p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <p className="font-semibold text-lg">In Progress</p>
          <p className="text-2xl">{inprogress}</p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <p className="font-semibold text-lg">Expired</p>
          <p className="text-2xl">{expired}</p>
        </div>
      </div>
      <div>
        <p className="font-semibold text-lg mb-2 text-center">
          Status Info by Proposal Class
        </p>
        <div className="w-full h-64">
          {selectedClass && subclassChartData ? (
            <Bar
              data={subclassChartData}
              options={{ maintainAspectRatio: false, responsive: true }}
            />
          ) : (
            <Bar
              data={groupedBarData}
              options={{ maintainAspectRatio: false, responsive: true, onClick: handleBarClick }}
            />
          )}
        </div>
        {selectedClass && (
          <button
            className="mt-2 px-4 py-2 bg-gray-300 rounded"
            onClick={() => setSelectedClass(null)}
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
};

// --- Main ProposalList Component --- //
const ProposalList = () => {
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [options, setOptions] = useState({});
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const handleDepartmentFilter = (department) => {
    setSelectedDepartment(department);
    if (department === 'Repair') {
      setFilteredProposals(proposals.filter(p => p.proposalSubClass?.proposalClass?.className === 'Repair Work'));
    } else if (department === 'New Construction') {
      setFilteredProposals(proposals.filter(p => ['New Construction', 'Modernization'].includes(p.proposalSubClass?.proposalClass?.className)));
    } else {
      setFilteredProposals(proposals);
    }
  };

  useEffect(() => {
    handleDepartmentFilter(selectedDepartment);
  }, [proposals]);

  // Fetch building options
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const [buildingsRes] = await Promise.all([
          fetch(API_BASE_URL + "/api/setup/buildings/all", {
            credentials: "include",
          }).then((res) => res.json()),
        ]);
        setOptions((prev) => ({
          ...prev,
          building: buildingsRes || [],
        }));
      } catch (error) {
        console.error("Error fetching building dropdown options:", error);
      }
    };
    fetchBuildings();
  }, []);

  // Fetch additional dropdown options
  useEffect(() => {
    const getProposalClassesFromSubClasses = (subClasses) => {
      const uniqueClasses = new Map();
      subClasses.forEach((subClass) => {
        const { classId, className } = subClass.proposalClass;
        if (!uniqueClasses.has(classId)) {
          uniqueClasses.set(classId, { classId, className });
        }
      });
      return Array.from(uniqueClasses.values());
    };

    const fetchOptions = async () => {
      try {
        const [salesRepRes, contactRes, proposalSubClassRes] = await Promise.all([
          fetch(API_BASE_URL + "/api/setup/salesperson/salespersons", {
            credentials: "include",
          }).then((res) => res.json()),
          fetch(API_BASE_URL + "/api/setup/contacts/get-all", {
            credentials: "include",
          }).then((res) => res.json()),
          fetch(API_BASE_URL + "/api/proposals-and-jobs/subclasses", {
            credentials: "include",
          }).then((res) => res.json()),
        ]);

        setOptions((prev) => ({
          ...prev,
          salesPerson: salesRepRes || [],
          proposalSubClass: proposalSubClassRes || [],
          proposalClass: getProposalClassesFromSubClasses(proposalSubClassRes) || [],
          contact: contactRes || [],
        }));
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };
    fetchOptions();
  }, []);

  // Modal controls
  const handleOpenViewModal = (proposal, index) => {
    setSelectedProposal(proposal);
    setSelectedIndex(index);
    setIsViewOpen(true);
  };

  const handleNext = () => {
    if (selectedIndex < filteredProposals.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setSelectedProposal(filteredProposals[selectedIndex + 1]);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setSelectedProposal(filteredProposals[selectedIndex - 1]);
    }
  };

  const handleCloseViewModal = () => setIsViewOpen(false);

  // Fetch Proposals
  const fetchProposals = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/proposals-and-jobs/proposals`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      const proposalsData = Array.isArray(data) ? data : [];
      setProposals(proposalsData);
      setFilteredProposals(proposalsData);
    } catch (error) {
      console.error("Fetch error: ", error);
      setProposals([]);
      setFilteredProposals([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Search Functionality
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value === "") {
      setFilteredProposals(proposals);
    } else {
      setFilteredProposals(
        proposals.filter((proposal) =>
          proposal.proposalNumber.toString().includes(e.target.value)
        )
      );
    }
  };

  return (
    <div className="mx-auto pl-4 pr-4 h-screen w-full flex">
      {/* Side Panel */}
      <div className="w-1/4 pr-4">
        <DashboardSidePanel proposals={filteredProposals} />
      </div>

      {/* Main Content */}
      <div className="w-3/4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Proposals List</h2>
          <div className="flex justify-end gap-4">
            {/* New Buttons */}
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => handleDepartmentFilter('Repair')}
            >
              Repair Department
            </button>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => handleDepartmentFilter('New Construction')}
            >
              Mod/New Construction Department
            </button>
            {/* Search Bar */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden w-72 flex-shrink-0">
              <div className="px-2 py-2 text-gray-500 cursor-pointer">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="px-2 py-2 w-full outline-none"
                placeholder="Search by Proposal Number"
                value={searchQuery}
                onChange={handleSearchChange}
                readOnly={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {!isLoading ? (
          <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
            <thead className="bg-gray-200">
              <tr>
                {proposalListFields.map((field) => (
                  <th
                    key={field.key}
                    className="px-4 py-2 text-left font-bold text-blue-600"
                  >
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {filteredProposals.map((proposal, index) => (
                <tr key={proposal.proposalNumber} className="border-t">
                  {proposalListFields.map((field) => {
                    if (field.key === "actions") {
                      return (
                        <td key={field.key} className="px-4 py-2">
                          <button
                            className="text-blue-600"
                            onClick={() => handleOpenViewModal(proposal, index)}
                          >
                            View
                          </button>
                        </td>
                      );
                    }
                    let displayValue = "";
                    if (field.subKeys && Array.isArray(field.subKeys)) {
                      const parentValue = proposal[field.key] || {};
                      displayValue = field.subKeys
                        .map((subKey) => parentValue[subKey])
                        .filter(Boolean)
                        .join(" ");
                    } else if (
                      field.options &&
                      Array.isArray(field.options)
                    ) {
                      const matchedOption = field.options.find(
                        (opt) => opt.key === proposal[field.key]
                      );
                      displayValue =
                        matchedOption?.label || proposal[field.key];
                    } else if (field.type === "datetime-local") {
                      const rawDate = proposal[field.key];
                      if (rawDate) {
                        const dateObj = new Date(rawDate);
                        const pad = (n) => n.toString().padStart(2, "0");
                        const mm = pad(dateObj.getMonth() + 1);
                        const dd = pad(dateObj.getDate());
                        const yyyy = dateObj.getFullYear();
                        const formattedDate = `${mm}/${dd}/${yyyy}`;
                        if (field.isDateOnly) {
                          displayValue = formattedDate;
                        } else {
                          const hours = pad(dateObj.getHours());
                          const minutes = pad(dateObj.getMinutes());
                          displayValue = `${formattedDate} ${hours}:${minutes}`;
                        }
                      }
                    } else {
                      displayValue = proposal[field.key];
                    }
                    return (
                      <td key={field.key} className="px-4 py-2">
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex justify-center items-center mt-6">
            <p>Loading...</p>
          </div>
        )}

        {/* Modal */}
        <ViewEditProposalsModal
          open={isViewOpen}
          handleClose={handleCloseViewModal}
          proposal={selectedProposal}
          setProposal={setSelectedProposal}
          setNext={handleNext}
          setPrevious={handlePrevious}
          allOptions={options}
        />
      </div>
    </div>
  );
};

export default ProposalList;
